// react
import { useContext, useEffect, useState } from 'react';

// router
import { useNavigate, useParams } from 'react-router-dom';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextBanner } from '../../../App.context';
import { ContextProject } from '../Project/Project.context';

// types
import { SpendPlan } from '../../../@types';

// services
import { fetchSpendPlan } from '../../../services/api';

// components
import { HeaderDetailPage } from '../../ui/HeaderDetailPage/HeaderDetailPage';
import { FormCreateSpendPlan } from '../../forms/FormCreateSpendPlan/FormCreateSpendPlan';
import { BlockchainProcessing } from '../../ui/BlockchainProcessing/BlockchainProcessing';

// mui
import { Box, Button, Grid, Paper, Skeleton, Stack } from '@mui/material';

// icons
import { Payments as IconSpendPlan, ArrowBack as IconBack } from '@mui/icons-material';

// data
import { spacing } from '../../../utils/designSystem';

// Component

export const PageCreateSpendPlan = () => {
	// hooks
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { spendPlanId } = useParams<{ spendPlanId: string }>();
	const spendPlanIdNumber = spendPlanId ? Number(spendPlanId) : null;

	// context
	const { setBanner } = useContext(ContextBanner);
	const { project } = useContext(ContextProject);

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// Check if we're editing an existing spend plan
	const isEditMode = Boolean(spendPlanIdNumber);

	// state
	const [isLoading, setIsLoading] = useState(false);
	const [spendPlan, setSpendPlan] = useState<SpendPlan | null>(null);
	const [isFetchingSpendPlan, setIsFetchingSpendPlan] = useState(isEditMode);

	// Fetch spend plan by ID when editing
	useEffect(() => {
		const loadSpendPlan = async () => {
			if (!isEditMode || !spendPlanIdNumber || !accessToken || !idToken) {
				setIsFetchingSpendPlan(false);
				return;
			}

			setIsFetchingSpendPlan(true);
			try {
				const result = await fetchSpendPlan({ accessToken, idToken }, spendPlanIdNumber, {
					populate: 'account,activityAllocations,contractAllocations',
				});

				if (result.error) {
					setBanner({
						message: result.error.message || 'Failed to load spend plan',
						severity: 'error',
					});
					setSpendPlan(null);
				} else {
					setSpendPlan(result.data || null);
				}
			} catch (error) {
				setBanner({
					message: 'Failed to load spend plan',
					severity: 'error',
				});
				setSpendPlan(null);
			} finally {
				setIsFetchingSpendPlan(false);
			}
		};

		loadSpendPlan();
	}, [isEditMode, spendPlanIdNumber, accessToken, idToken, setBanner]);

	// handlers
	const handleBack = () => {
		navigate(`/project/${project?.idWbg}/spend-plans/spend-planning`);
	};

	const handleSuccess = (spendPlanId?: number) => {
		setBanner({
			message: isEditMode
				? t('spendPlan.spendPlanUpdatedSuccessfully')
				: t('spendPlan.spendPlanCreatedSuccessfully'),
			severity: 'success',
		});
		if (!isEditMode && spendPlanId) {
			navigate(`/project/${project?.idWbg}/spend-plans/spend-planning/${spendPlanId}`);
		} else {
			navigate(`/project/${project?.idWbg}/spend-plans/spend-planning`);
		}
	};

	// Build breadcrumb path
	const breadcrumbPath = `/project/${project?.idWbg}/spend-plans/spend-planning`;

	// Show loading skeleton while fetching spend plan for edit
	if (isFetchingSpendPlan) {
		return (
			<Box>
				{/* Header Skeleton */}
				<Box
					sx={{
						bgcolor: 'grey.50',
						borderBottom: '1px solid',
						borderColor: 'divider',
						px: 3,
						py: 3,
					}}>
					<Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
						<Skeleton variant="text" width={120} height={20} />
					</Stack>
					<Stack direction="row" alignItems="center" spacing={2}>
						<Skeleton variant="circular" width={48} height={48} />
						<Box sx={{ flex: 1 }}>
							<Skeleton variant="text" width={200} height={32} />
						</Box>
						<Skeleton variant="rounded" width={80} height={36} sx={{ mr: 1 }} />
						<Skeleton variant="rounded" width={80} height={36} />
					</Stack>
				</Box>
				{/* Form Skeleton */}
				<Box sx={{ px: 3, py: 3 }}>
					<Grid container spacing={3}>
						{/* Left Column */}
						<Grid item xs={12} md={7}>
							<Paper
								elevation={0}
								sx={{
									border: '1px solid',
									borderColor: 'divider',
									borderRadius: 2,
									p: 2.5,
									mb: 3,
								}}>
								<Skeleton variant="text" width={150} height={28} sx={{ mb: 2 }} />
								<Skeleton variant="rounded" width="100%" height={56} sx={{ mb: 2 }} />
								<Skeleton variant="rounded" width="100%" height={56} />
							</Paper>
							<Paper
								elevation={0}
								sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2.5 }}>
								<Skeleton variant="text" width={150} height={28} sx={{ mb: 2 }} />
								<Stack direction="row" spacing={2}>
									<Skeleton variant="rounded" width="50%" height={56} />
									<Skeleton variant="rounded" width="50%" height={56} />
								</Stack>
							</Paper>
						</Grid>
						{/* Right Column */}
						<Grid item xs={12} md={5}>
							<Paper
								elevation={0}
								sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2.5 }}>
								<Skeleton variant="text" width={150} height={28} sx={{ mb: 2 }} />
								<Stack spacing={2}>
									{[1, 2, 3, 4].map((i) => (
										<Stack key={i} direction="row" justifyContent="space-between">
											<Skeleton variant="text" width={100} height={20} />
											<Skeleton variant="text" width={80} height={20} />
										</Stack>
									))}
								</Stack>
							</Paper>
						</Grid>
					</Grid>
				</Box>
			</Box>
		);
	}

	return (
		<Box>
			{/* Header */}
			<HeaderDetailPage
				icon={IconSpendPlan}
				breadcrumb={{
					label: t('spendPlan.spendPlanning'),
					to: breadcrumbPath,
				}}
				title={isEditMode ? t('spendPlan.editSpendPlan') : t('spendPlan.createSpendPlan')}
				variant="financial"
				customAction={
					<Stack direction="row" spacing={1.5}>
						<Button
							variant="outlined"
							startIcon={<IconBack />}
							onClick={handleBack}
							sx={{
								borderRadius: 2,
								textTransform: 'none',
								fontWeight: 600,
							}}>
							{t('cancel')}
						</Button>
						<Button
							variant="contained"
							form={isEditMode ? 'form-edit-spend-plan' : 'form-create-spend-plan'}
							type="submit"
							disabled={isLoading}
							sx={{
								borderRadius: 2,
								textTransform: 'none',
								fontWeight: 600,
							}}>
							{isEditMode ? t('spendPlan.saveSpendPlan') : t('spendPlan.createSpendPlan')}
						</Button>
					</Stack>
				}
			/>

			{/* Form Content */}
			<Box sx={{ px: { xs: spacing.sm, sm: spacing.md }, py: spacing.md }}>
				{/* Creating a spend plan writes to the chain (account + envelope); show the
				    same on-chain animation as v1 while that is in flight. The form stays
				    mounted (just hidden) so the in-flight submit keeps its closures. */}
				{isLoading && !isEditMode && <BlockchainProcessing status="LOADING" />}
				<Box sx={{ display: isLoading && !isEditMode ? 'none' : 'block' }}>
					<FormCreateSpendPlan
						onSuccess={handleSuccess}
						setIsLoading={setIsLoading}
						spendPlan={spendPlan}
					/>
				</Box>
			</Box>
		</Box>
	);
};
